import { FC, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import ConfigEditorShell from "../../components/ConfigEditorShell/ConfigEditorShell";
import {
	useConfigEditor,
	type ExitRoute,
} from "../../components/ConfigEditorShell/useConfigEditor";
import getIcon, { NAV_ICON_NAMES } from "../../utils/get-icon";
import * as metrics from "./designMetrics";
import { IAction } from "../../domain/action.interface";
import { IActionGroup } from "../../domain/action-group.interface";
import { findActionCodeConflict, normaliseActionCode } from "./actionCode";

/**
 * SIMS-8687 — the full-page action editor.
 *
 * Replaces the inline `Card` the Actions screen carried. Every one of this story's TestRail cases
 * is written against "the full-page editor", and the four AC-EDITOR cases (C17728-C17731) name
 * affordances the inline card had none of: a breadcrumb, a sticky Cancel/Save header, an
 * "Unsaved changes" pill, and one discard prompt shared by every exit route. Those belong to the
 * Foundation epic's `ConfigEditorShell` / `useConfigEditor` (SIMS-8614), which the Payers editor
 * already uses — so this is a migration to the shared pattern, not a new one, exactly as
 * SIMS-8678 migrated the list to `ConfigListShell`.
 *
 * Layout follows Hi-Fi `actions-12-action-editor-nav-order-icon` and
 * `actions-13-action-editor-non-nav-type`: an "Action details" card (Code + Name, Description,
 * Type + Link, then Order + Icon only for a navigation entry) followed by a "Group *" card whose
 * options are tiles. The helper lines under Code, Description, Order and Icon are the export's own
 * words — the one under Code is load-bearing, because C17717 requires the screen to SAY codes are
 * fixed rather than just disable the field.
 *
 * Deliberately NOT react-hook-form, for the reason PayerEditor gives: dirty state has to be the
 * shell's single source of truth, and a second copy of it is how the pill and the discard prompt
 * come to disagree.
 */

/** The reference types. Kept in step with the list screen's own copy. */
export const ACTION_TYPES = [
	"MENU_OPTION",
	"KPI",
	"ALERTS",
	"OPTION",
	"EVENTS",
	"SPECIAL_FEATURE",
	"REPORT",
	"System",
];

/**
 * The type that puts an action in the left navigation, and so the only one carrying a position and
 * an icon (FR-19 / FR-22, cases C17719-C17723).
 *
 * NOTE, recorded for the PO: Hi-Fi `actions-13`, the "no Order or Icon" state, is captioned
 * "alert type" in the prototype's own review bar but its Type select still reads MENU_OPTION. Both
 * cannot be right. The caption is followed here — MENU_OPTION is the navigation type throughout
 * this product, including the `order`/`icon` gate the screen already shipped — and the export's
 * select is treated as a prototype slip.
 */
export const NAVIGATION_ACTION_TYPE = "MENU_OPTION";

export interface ActionDraft extends Record<string, unknown> {
	code: string;
	name: string;
	description: string;
	type: string;
	groupId: string;
	link: string;
	order: number;
	icon: string;
	subActions: string[];
}

export interface ActionEditorProps {
	/** The action being edited, or null to create one. */
	action: IAction | null;
	/** Active groups — the only offerable ones (FR-12: the group has to be usable). */
	groups: IActionGroup[];
	/** Every action in the catalogue, for the AC2 uniqueness check. */
	allActions: IAction[];
	/** Actions offerable as children (FR-17: navigation entries only). */
	subActionOptions: IAction[];
	onSubmit: (draft: ActionDraft) => Promise<void>;
	/** Leave the editor — the shell has already cleared any unsaved-changes prompt. */
	onExit: () => void;
	onDelete?: () => void;
}

type FieldErrors = Partial<
	Record<"code" | "name" | "description" | "type" | "groupId" | "order", string>
>;

/**
 * A section-card title. Rendered as a real `h2` (`component="h2"`), not a styled paragraph: the
 * editor's own h1 is the record's name, so these are the sections under it, and the design draws
 * them as headings. As plain Typography they were invisible to a screen reader's heading
 * navigation and to `getByRole("heading")` alike — which is how C17732 caught it.
 */
/** A selectable tile — the Group options and the icon swatches share these metrics. */
const tileSx = (selected: boolean) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	gap: 0.75,
	minHeight: "2.5rem",
	padding: "0 0.75rem",
	borderRadius: "0.5rem",
	border: "1px solid",
	borderColor: selected ? "primary.main" : "divider",
	backgroundColor: selected
		? (t: { palette: { primary: { main: string } } }) =>
				alpha(t.palette.primary.main, 0.08)
		: "transparent",
	color: selected ? "primary.main" : "text.primary",
	fontSize: "0.8125rem",
	fontWeight: selected ? 600 : 500,
	cursor: "pointer",
	font: "inherit",
});

const draftFromAction = (action: IAction | null): ActionDraft => ({
	code: action?.code ?? "",
	name: action?.name ?? "",
	description: action?.description ?? "",
	type: action?.type ?? NAVIGATION_ACTION_TYPE,
	groupId:
		(typeof action?.groupId === "string"
			? action.groupId
			: action?.groupId?._id) ?? "",
	link: action?.link ?? "",
	order: action?.order ?? 0,
	icon: action?.icon ?? "",
	subActions: ((action?.subActions ?? []) as Array<string | IAction>).map(
		(sub) => (typeof sub === "string" ? sub : sub._id),
	),
});

const ActionEditor: FC<ActionEditorProps> = ({
	action,
	groups,
	allActions,
	subActionOptions,
	onSubmit,
	onExit,
	onDelete,
}) => {
	const isNew = action === null;

	const initial = useMemo<ActionDraft>(() => draftFromAction(action), [action]);

	const editor = useConfigEditor<ActionDraft>({
		initial,
		onSave: onSubmit,
		// The action's own noun — the shared hook otherwise tells the user "The payer could not be
		// saved." when an action save rejects with nothing usable to say.
		fallbackSaveError: "The action could not be saved.",
	});

	/**
	 * Errors are only SURFACED once Save has been attempted. C17710 asks that the refusal ask the
	 * user to choose a group; covering an untouched create form in red before they have reached
	 * the fields is a different, worse thing.
	 */
	const [submitAttempted, setSubmitAttempted] = useState(false);

	// One question regardless of which affordance asked (C17729 / FR-4): Cancel, the breadcrumb,
	// navigating away and switching tab all come through here. The route is carried only so the
	// DESTINATION can differ, never the prompt.
	const leave = (route: ExitRoute) => editor.requestExit(onExit, route);

	const isNavigationType = editor.draft.type === NAVIGATION_ACTION_TYPE;
	// SIMS-8710 (FR-24): a stored type outside the reference list stays editable as free text and
	// is marked as such, rather than silently replaced by a default on open.
	const isRecognisedType = ACTION_TYPES.includes(editor.draft.type);

	// Read before the narrowing below, which resolves `action` to null inside the create branch.
	const editingId = action?._id;

	const errors = useMemo<FieldErrors>(() => {
		const next: FieldErrors = {};
		const draft = editor.draft;
		if (draft.code.trim() === "") {
			next.code = "Code is required";
		} else {
			// AC2 — normalise, THEN compare (C17715). Checked against the catalogue the screen
			// already holds, so the refusal is immediate and names the conflict; the backend's own
			// uniqueness rule still applies, and a 409 from it surfaces in the save-failed banner.
			//
			// `editingId` excludes the record being edited, so an existing action never reports its
			// own code as a duplicate. The code is immutable on edit (AC3), so that exclusion is
			// belt and braces rather than a live path — but a validator that raises a false
			// conflict is worse than one that never fires.
			const clash = findActionCodeConflict(allActions, draft.code, editingId);
			if (clash) {
				next.code = `${normaliseActionCode(draft.code)} is already used by "${clash.name}". Codes must be unique.`;
			}
		}
		if (draft.name.trim() === "") next.name = "Name is required";
		if (draft.description.trim() === "") {
			next.description = "Description is required";
		}
		if (draft.type.trim() === "") next.type = "Type is required";
		// AC1 / FR-12: a group is required, and it must be one of the ACTIVE groups — a draft
		// pointing at a deactivated group would file a permission under a section nobody sees.
		if (!groups.some((group) => group._id === draft.groupId)) {
			next.groupId = "Choose a group for this action";
		}
		if (
			isNavigationType &&
			(!Number.isInteger(draft.order) || draft.order < 0)
		) {
			next.order = "Position must be a whole number, zero or more";
		}
		return next;
	}, [editor.draft, allActions, groups, isNavigationType, editingId]);

	const show = (field: keyof FieldErrors): string | undefined =>
		submitAttempted ? errors[field] : undefined;

	/**
	 * C17721 — moving the type away from a navigation entry withdraws the icon field, and the
	 * icon goes with it.
	 *
	 * The case asks that "the user is not left with an orphaned icon value silently stored". A
	 * withdrawn field whose value still rides along on the save is exactly that: the administrator
	 * can no longer see it, cannot clear it, and `getIcon` would still resolve it if the type ever
	 * moved back. So the value is dropped WITH the field, in the same change, rather than lingering
	 * in the draft.
	 *
	 * `order` is deliberately left alone. The list already renders it as "—" for anything that is
	 * not a navigation entry, so a stale position displays as absent and means nothing — whereas a
	 * stale icon would silently reappear. Recorded on the case rather than guessed at.
	 */
	const changeType = (next: string): void => {
		if (next === NAVIGATION_ACTION_TYPE) {
			editor.setField("type", next);
			return;
		}
		editor.setDraft({ ...editor.draft, type: next, icon: "" });
	};

	const handleSave = (): void => {
		setSubmitAttempted(true);
		if (Object.keys(errors).length > 0) {
			// C17710 / C17714: refused, and every value the user entered stays on the form — the
			// draft is deliberately not touched here.
			return;
		}
		void editor.save();
	};

	/**
	 * SIMS-6516 — the SELECTED set resolves against EVERY action, not just the offerable ones.
	 * A child whose type has since moved away from a navigation entry is still attached, and
	 * dropping it from the control's value would silently detach it on the next save.
	 */
	const selectedSubActions = useMemo<IAction[]>(() => {
		const byId = new Map(
			allActions.map((candidate) => [candidate._id, candidate]),
		);
		return editor.draft.subActions
			.map((id) => byId.get(id))
			.filter((candidate): candidate is IAction => Boolean(candidate));
	}, [allActions, editor.draft.subActions]);

	/** Offerable (FR-17) plus whatever is already attached, so the control can render its value. */
	const subActionChoices = useMemo<IAction[]>(
		() => [
			...subActionOptions,
			...selectedSubActions.filter(
				(selected) =>
					!subActionOptions.some((option) => option._id === selected._id),
			),
		],
		[subActionOptions, selectedSubActions],
	);

	const [subFilter, setSubFilter] = useState("");

	/** The candidates, narrowed by the filter box — on name or code, as the group editor does. */
	const visibleSubChoices = useMemo<IAction[]>(() => {
		const needle = subFilter.trim().toLowerCase();
		if (!needle) return subActionChoices;
		return subActionChoices.filter(
			(candidate) =>
				candidate.name.toLowerCase().includes(needle) ||
				candidate.code.toLowerCase().includes(needle),
		);
	}, [subActionChoices, subFilter]);

	const toggleSubAction = (id: string): void => {
		const current = editor.draft.subActions;
		editor.setField(
			"subActions",
			current.includes(id)
				? current.filter((existing) => existing !== id)
				: [...current, id],
		);
	};

	return (
		<ConfigEditorShell
			discardBody="This action has changes that have not been saved. Discarding closes the editor and leaves the action as it was."
			breadcrumbs={[
				{ label: "Program setup" },
				{ label: "Actions", onClick: () => leave("breadcrumb") },
			]}
			title={isNew ? "New action" : (action?.name ?? "Action")}
			isDirty={editor.isDirty}
			isSaving={editor.isSaving}
			isNew={isNew}
			canSave={editor.canSave}
			saveError={editor.saveError}
			failedTitle="Action wasn't saved"
			discardPromptOpen={editor.discardPromptOpen}
			onSave={handleSave}
			onDelete={action && onDelete ? onDelete : undefined}
			onCancel={() => leave("cancel")}
			onBreadcrumb={() => leave("breadcrumb")}
			onKeepEditing={editor.keepEditing}
			requestExit={editor.requestExit}
			onDiscard={editor.discard}
		>
			<Box sx={metrics.editorCard}>
				<Typography component="h2" sx={metrics.editorCardTitle}>
					Action details
				</Typography>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
						gap: 2.5,
						alignItems: "flex-start",
					}}
				>
					<Box>
						<TextField
							fullWidth
							required
							label="Code"
							// AC3 (C17717): immutable once created. Disabled AND explained below —
							// the case asks that the user be TOLD, not left to infer it from a
							// greyed-out field.
							disabled={!isNew}
							value={editor.draft.code}
							error={Boolean(show("code"))}
							onChange={(e) => editor.setField("code", e.target.value)}
							// AC2 / FR-21: normalised on blur, not on each keystroke, so the caret
							// does not jump while the user is still typing. What the field shows is
							// what gets stored.
							onBlur={() =>
								editor.setField("code", normaliseActionCode(editor.draft.code))
							}
						/>
						<Typography
							sx={{
								...metrics.fieldHelper,
								color: show("code") ? "error.main" : "text.secondary",
							}}
						>
							{show("code") ?? "Uppercase, no spaces. Cannot be changed later."}
						</Typography>
					</Box>
					<TextField
						fullWidth
						required
						label="Name"
						value={editor.draft.name}
						error={Boolean(show("name"))}
						helperText={show("name")}
						onChange={(e) => editor.setField("name", e.target.value)}
					/>
				</Box>

				<Box>
					<TextField
						fullWidth
						required
						multiline
						rows={3}
						label="Description"
						value={editor.draft.description}
						error={Boolean(show("description"))}
						onChange={(e) => editor.setField("description", e.target.value)}
					/>
					<Typography
						sx={{
							...metrics.fieldHelper,
							color: show("description") ? "error.main" : "text.secondary",
						}}
					>
						{show("description") ??
							"Shown under the permission name on the Roles screen."}
					</Typography>
				</Box>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
						gap: 2.5,
						alignItems: "flex-start",
					}}
				>
					{!isNew && !isRecognisedType ? (
						<TextField
							fullWidth
							required
							label="Type"
							value={editor.draft.type}
							error={Boolean(show("type"))}
							helperText={
								show("type") ??
								"This existing type is not in the standard list."
							}
							onChange={(e) => editor.setField("type", e.target.value)}
						/>
					) : (
						<FormControl fullWidth required error={Boolean(show("type"))}>
							<InputLabel id="action-type-label">Type</InputLabel>
							<Select
								labelId="action-type-label"
								label="Type"
								value={editor.draft.type}
								onChange={(e: SelectChangeEvent) => changeType(e.target.value)}
							>
								{ACTION_TYPES.map((type) => (
									<MenuItem key={type} value={type}>
										{type}
									</MenuItem>
								))}
							</Select>
							{show("type") && <FormHelperText>{show("type")}</FormHelperText>}
						</FormControl>
					)}
					<TextField
						fullWidth
						label="Link"
						value={editor.draft.link}
						onChange={(e) => editor.setField("link", e.target.value)}
					/>
				</Box>

				{/* AC4 + AC5 (FR-22, cases C17719-C17723): position and icon belong to a navigation
				    entry and to nothing else. Withdrawn together, as actions-13 shows them absent
				    together. */}
				{isNavigationType && (
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", md: "9rem 1fr" },
							gap: 2.5,
							alignItems: "flex-start",
						}}
					>
						<Box>
							{/*
							 * The label sits ABOVE the field, free of the border, the way the
							 * design draws it — and the way the Icon label beside it already did.
							 *
							 * MUI notches its label INTO the top border, which starts the control
							 * lower than a plain label does. That is what threw this row out:
							 * "Position in the left navigation." landed above "The icon this
							 * action shows in the left navigation." even though both sit directly
							 * under their control.
							 *
							 * The accessible name is unchanged, so every getByLabel(/^Order/)
							 * still resolves — `inputProps.aria-label` names the input exactly as
							 * the MUI label did.
							 */}
							<Typography sx={metrics.fieldLabel}>Order</Typography>
							<TextField
								fullWidth
								type="number"
								inputProps={{ min: 0, "aria-label": "Order" }}
								value={
									Number.isNaN(editor.draft.order) ? "" : editor.draft.order
								}
								error={Boolean(show("order"))}
								onChange={(e) =>
									editor.setField("order", Number.parseInt(e.target.value, 10))
								}
							/>
							<Typography
								sx={{
									...metrics.fieldHelper,
									color: show("order") ? "error.main" : "text.secondary",
								}}
							>
								{show("order") ?? "Position in the left navigation."}
							</Typography>
						</Box>

						<Box>
							{/* The same label treatment as Order beside it — one definition, so the
							    two cannot drift apart again. */}
							<Typography
								component="span"
								id="action-icon-label"
								sx={metrics.fieldLabel}
							>
								Icon
							</Typography>
							{/*
							 * A PICKER, not a text field (AC4, actions-12). The vocabulary is
							 * `NAV_ICON_NAMES`, exported from the navigation's own resolver: a name
							 * outside it renders NOTHING in the left navigation, so the free-text
							 * field this replaces let an administrator save an icon that never
							 * appeared.
							 *
							 * OQ-5 is open — the export draws roughly fourteen glyphs and they do
							 * not map one to one onto these twelve, and the owners (Qori + the dev
							 * team) have yet to confirm the vocabulary. Built from the set that
							 * WORKS rather than the set that is drawn; recorded on C17719/C17732.
							 */}
							<Box
								role="group"
								aria-labelledby="action-icon-label"
								sx={metrics.iconRowControl}
							>
								{NAV_ICON_NAMES.map((name) => {
									const selected = editor.draft.icon === name;
									return (
										<Box
											key={name}
											component="button"
											type="button"
											aria-label={name}
											aria-pressed={selected}
											onClick={() =>
												// Re-selecting the chosen one clears it: the icon is
												// optional, and without this there is no way back to no
												// icon once one has been picked.
												editor.setField("icon", selected ? "" : name)
											}
											sx={metrics.iconTile(selected)}
										>
											{getIcon(name)}
										</Box>
									);
								})}
							</Box>
							<Typography sx={metrics.fieldHelper}>
								The icon this action shows in the left navigation.
							</Typography>
						</Box>
					</Box>
				)}
			</Box>

			{/* AC1 (FR-12): a group is required, and actions-12/13 render the options as tiles
			    rather than a dropdown — the section an action lands in is the decision the editor
			    is really asking for, so it is shown rather than hidden behind a menu. */}
			<Box sx={metrics.editorCard}>
				<Typography component="h2" sx={metrics.editorCardTitle}>
					Group *
				</Typography>
				<Typography sx={{ ...metrics.fieldHelper, mt: 0 }}>
					The section this action appears under when a role&apos;s permissions
					are edited.
				</Typography>
				<Box
					role="radiogroup"
					aria-label="Group"
					sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
				>
					{groups.map((group) => {
						const selected = editor.draft.groupId === group._id;
						return (
							<Box
								key={group._id}
								component="button"
								type="button"
								role="radio"
								aria-checked={selected}
								onClick={() => editor.setField("groupId", group._id)}
								sx={tileSx(selected)}
							>
								{group.name}
							</Box>
						);
					})}
				</Box>
				{show("groupId") && (
					<Typography
						sx={{ ...metrics.fieldHelper, mt: 0, color: "error.main" }}
						role="alert"
					>
						{show("groupId")}
					</Typography>
				)}
			</Box>

			{/*
			 * Sub-actions as its own card with the candidates SHOWN, not an Autocomplete.
			 *
			 * The design gives it a card after Group, a filter box, and a two-column
			 * checkbox list in a bordered box at `max-height: 300px; overflow-y: auto`
			 * (`Actions Pattern v2.dc.html:726`). It was a multi-select dropdown, which
			 * hides the candidates behind a click — and these are permissions that become
			 * this action's menu items, so which ones exist is the thing being decided.
			 *
			 * Same construction as the group editor's membership list, deliberately: they
			 * are the same job on two screens.
			 */}
			<Box sx={metrics.editorCard}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<Box>
						<Typography component="h2" sx={metrics.editorCardTitle}>
							Sub-actions
						</Typography>
						<Typography sx={{ ...metrics.fieldHelper, mt: "2px" }}>
							{selectedSubActions.length === 0
								? "None selected. Only menu options can be sub-actions — they become this action's dropdown items in the app menu."
								: `${selectedSubActions.length} selected. Only menu options can be sub-actions — they become this action's dropdown items in the app menu.`}
						</Typography>
					</Box>
					<Box sx={{ flex: 1 }} />
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{
							backgroundColor: (theme) => theme.palette.surface.main,
							borderRadius: "0.5rem",
							height: "2.5rem",
							px: 1.5,
							width: "17.5rem",
							maxWidth: "100%",
						}}
					>
						<SearchIcon
							sx={{ fontSize: "1.125rem", color: "text.secondary" }}
						/>
						<InputBase
							value={subFilter}
							onChange={(event) => setSubFilter(event.target.value)}
							placeholder="Filter actions"
							inputProps={{ "aria-label": "Filter actions" }}
							sx={{ flex: 1, fontSize: "0.8125rem" }}
						/>
					</Stack>
				</Stack>

				<Box
					sx={{
						border: "1px solid",
						borderColor: (theme) => theme.palette.outline.main,
						borderRadius: "0.5rem",
						maxHeight: "18.75rem",
						overflowY: "auto",
					}}
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
						}}
					>
						{visibleSubChoices.map((candidate) => {
							const checked = editor.draft.subActions.includes(candidate._id);
							return (
								<Stack
									key={candidate._id}
									direction="row"
									alignItems="center"
									spacing={1.25}
									onClick={() => toggleSubAction(candidate._id)}
									sx={{
										px: 2,
										py: 1.25,
										cursor: "pointer",
										borderBottom: "1px solid",
										borderColor: "divider",
										"&:hover": {
											backgroundColor: (theme) =>
												alpha(theme.palette.primary.main, 0.06),
										},
									}}
								>
									<Checkbox
										size="small"
										checked={checked}
										onClick={(event) => event.stopPropagation()}
										onChange={() => toggleSubAction(candidate._id)}
										inputProps={{
											"aria-label": `Sub-action ${candidate.name}`,
										}}
										sx={{ p: 0 }}
									/>
									<Box sx={{ minWidth: 0 }}>
										<Typography
											sx={{
												fontSize: "0.8125rem",
												fontWeight: 500,
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
										>
											{candidate.name}
										</Typography>
										<Typography
											sx={{
												fontFamily: "'SF Mono', Menlo, Consolas, monospace",
												fontSize: "0.625rem",
												color: "text.secondary",
											}}
										>
											{candidate.code}
										</Typography>
									</Box>
								</Stack>
							);
						})}
						{visibleSubChoices.length === 0 && (
							<Typography
								sx={{ fontSize: "0.8125rem", color: "text.secondary", p: 2 }}
							>
								{subFilter.trim()
									? `No menu option matches “${subFilter}”.`
									: "No other menu option is available to nest here."}
							</Typography>
						)}
					</Box>
				</Box>
			</Box>
		</ConfigEditorShell>
	);
};

export default ActionEditor;
